import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Button from "@scripts/react/components/Button";
import { Chip } from "@scripts/react/components/Chip";
import InlineAlert from "@scripts/react/components/InlineAlert";
import { RfModal } from "@scripts/react/components/RfModal/RfModal";
import { twClassNames } from "@scripts/react/helpers/twClassNames";
import { Dropdown, type DropdownItem } from "@components/Dropdown";
import {
    buildResolutionOptions,
    findDefaultResolution,
    getWebcamTrackInfo,
    type ResolutionOption
} from "@components/Workflows/EditorComponents/VideoPreview/webcamResolutionUtils";
import { MAX_VIDEO_DURATION } from "@rapid/constants/media";

type CaptureMode = "photo" | "video";

type WebcamResolution = { width: number; height: number };

type WebcamCaptureModalProps = {
    isOpen: boolean;
    onClose: () => void;
    onCapture: (files: File[]) => void;
};

const DEFAULT_RESOLUTION: WebcamResolution = { width: 1280, height: 720 };

const getSupportedVideoMimeType = () => {
    if (typeof MediaRecorder === "undefined") return "";

    return (
        ["video/webm;codecs=vp9", "video/webm;codecs=vp8", "video/webm", "video/mp4"].find(
            (mimeType) => MediaRecorder.isTypeSupported(mimeType)
        ) || ""
    );
};

const getUploadVideoMimeType = (recordingMimeType: string) => {
    if (recordingMimeType.startsWith("video/mp4")) return "video/mp4";
    return "video/webm";
};

const getCameraLabel = (camera: MediaDeviceInfo, index: number) =>
    camera.label.replace(/\(.+\)/, "").trim() || `Camera ${index + 1}`;

const resolutionValue = (resolution: WebcamResolution) =>
    `${resolution.width}x${resolution.height}`;

const resolutionLabel = (resolution: WebcamResolution | null) =>
    resolution ? `${resolution.width}×${resolution.height}` : "Auto";

const selectorTitle = (icon: string, label: string) =>
    (
        <span className="flex min-w-0 items-center gap-2">
            <i className={twClassNames(icon, "text-gray-500")} />
            <span className="truncate">{label}</span>
        </span>
    ) as unknown as string;

export const WebcamCaptureModal = ({ isOpen, onClose, onCapture }: WebcamCaptureModalProps) => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const streamRef = useRef<MediaStream | null>(null);
    const recorderRef = useRef<MediaRecorder | null>(null);
    const chunksRef = useRef<Blob[]>([]);
    const stopTimeoutRef = useRef<number | null>(null);
    const isOpenRef = useRef(isOpen);
    const selectedDeviceIdRef = useRef<string | null>(null);
    const resolutionRef = useRef<WebcamResolution | null>(DEFAULT_RESOLUTION);
    const pendingResolutionCameraRef = useRef<string | null>(null);
    const startCameraRequestRef = useRef(0);
    const supportedVideoMimeType = useMemo(getSupportedVideoMimeType, []);
    const canRecordVideo = typeof MediaRecorder !== "undefined" && Boolean(supportedVideoMimeType);
    const [mode, setMode] = useState<CaptureMode>(() => (canRecordVideo ? "video" : "photo"));
    const [cameras, setCameras] = useState<MediaDeviceInfo[]>([]);
    const [selectedDeviceId, setSelectedDeviceId] = useState<string | null>(null);
    const [resolution, setResolution] = useState<WebcamResolution | null>(DEFAULT_RESOLUTION);
    const [resolutionOptions, setResolutionOptions] = useState<ResolutionOption[]>([]);
    const [isLoadingCameras, setIsLoadingCameras] = useState(false);
    const [isLoadingResolutions, setIsLoadingResolutions] = useState(false);
    const [hasLoadedCameras, setHasLoadedCameras] = useState(false);
    const [isStartingCamera, setIsStartingCamera] = useState(false);
    const [isRecording, setIsRecording] = useState(false);
    const [recordingSeconds, setRecordingSeconds] = useState(0);
    const [error, setError] = useState<string | null>(null);
    const [resolutionWarning, setResolutionWarning] = useState<string | null>(null);

    useEffect(() => {
        isOpenRef.current = isOpen;
    }, [isOpen]);

    useEffect(() => {
        if (isOpen && !canRecordVideo) {
            setMode("photo");
        }
    }, [canRecordVideo, isOpen]);

    useEffect(() => {
        selectedDeviceIdRef.current = selectedDeviceId;
    }, [selectedDeviceId]);

    useEffect(() => {
        resolutionRef.current = resolution;
    }, [resolution]);

    const stopStream = useCallback(() => {
        streamRef.current?.getTracks().forEach((track) => track.stop());
        streamRef.current = null;

        if (videoRef.current) {
            videoRef.current.srcObject = null;
        }
    }, []);

    const clearStopTimeout = useCallback(() => {
        if (stopTimeoutRef.current) {
            window.clearTimeout(stopTimeoutRef.current);
            stopTimeoutRef.current = null;
        }
    }, []);

    const closeAndReset = useCallback(() => {
        const recorder = recorderRef.current;
        if (recorder) {
            recorder.ondataavailable = null;
            recorder.onstop = null;

            if (recorder.state === "recording") {
                recorder.stop();
            }

            recorderRef.current = null;
        }
        clearStopTimeout();
        startCameraRequestRef.current += 1;
        pendingResolutionCameraRef.current = null;
        chunksRef.current = [];
        setIsRecording(false);
        setRecordingSeconds(0);
        setHasLoadedCameras(false);
        setIsLoadingCameras(false);
        setIsLoadingResolutions(false);
        setIsStartingCamera(false);
        setMode(canRecordVideo ? "video" : "photo");
        stopStream();
        onClose();
    }, [canRecordVideo, clearStopTimeout, onClose, stopStream]);

    const getVideoConstraints = useCallback(
        (deviceId: string | null, targetResolution: WebcamResolution | null) => ({
            deviceId: deviceId ? { exact: deviceId } : undefined,
            facingMode: deviceId ? undefined : "user",
            width: { ideal: targetResolution?.width ?? DEFAULT_RESOLUTION.width },
            height: { ideal: targetResolution?.height ?? DEFAULT_RESOLUTION.height }
        }),
        []
    );

    const loadResolutionsForCamera = useCallback(
        async (cameraId: string, preferredResolution: WebcamResolution | null) => {
            pendingResolutionCameraRef.current = cameraId;
            setIsLoadingResolutions(true);
            setResolutionWarning(null);

            try {
                const trackInfo = await getWebcamTrackInfo(cameraId);

                if (pendingResolutionCameraRef.current !== cameraId) {
                    return;
                }

                const options = buildResolutionOptions(trackInfo);
                const defaultOption = findDefaultResolution(options, preferredResolution);
                const nextResolution = defaultOption
                    ? { width: defaultOption.width, height: defaultOption.height }
                    : trackInfo.settings.width && trackInfo.settings.height
                      ? { width: trackInfo.settings.width, height: trackInfo.settings.height }
                      : DEFAULT_RESOLUTION;

                setResolutionOptions(
                    options.length
                        ? options
                        : [{ ...nextResolution, label: resolutionLabel(nextResolution) }]
                );
                setResolution(nextResolution);

                const capsMaxWidth = (trackInfo.capabilities as any)?.width?.max ?? 0;
                const actualWidth = trackInfo.settings?.width ?? 0;

                if (capsMaxWidth >= 1280 && actualWidth <= 640) {
                    setResolutionWarning(
                        `Camera started at ${actualWidth}×${trackInfo.settings?.height}. Close other tabs using it if the preview looks low-res.`
                    );
                }
            } catch (cameraError) {
                if (pendingResolutionCameraRef.current !== cameraId) {
                    return;
                }

                console.error("Failed to get webcam capabilities", cameraError);
                setResolutionOptions([{ ...DEFAULT_RESOLUTION, label: "HD (1280×720)" }]);
                setResolution(DEFAULT_RESOLUTION);
            } finally {
                if (pendingResolutionCameraRef.current === cameraId) {
                    setIsLoadingResolutions(false);
                }
            }
        },
        []
    );

    const loadCameras = useCallback(async () => {
        setIsLoadingCameras(true);
        setHasLoadedCameras(false);
        setError(null);

        try {
            const permissionStream = await navigator.mediaDevices.getUserMedia({
                audio: false,
                video: { width: { ideal: 1280 }, height: { ideal: 720 } }
            });
            permissionStream.getTracks().forEach((track) => track.stop());

            const devices = await navigator.mediaDevices.enumerateDevices();
            const videoDevices = devices.filter((device) => device.kind === "videoinput");

            setCameras(videoDevices);

            if (videoDevices.length === 0) {
                setError("No cameras found.");
                return;
            }

            const currentDeviceIsValid =
                selectedDeviceIdRef.current &&
                videoDevices.some((device) => device.deviceId === selectedDeviceIdRef.current);
            const nextDeviceId = currentDeviceIsValid
                ? selectedDeviceIdRef.current
                : (videoDevices[0]?.deviceId ?? null);

            setSelectedDeviceId(nextDeviceId);
            if (nextDeviceId) {
                await loadResolutionsForCamera(nextDeviceId, resolutionRef.current);
            }
        } catch (cameraError: any) {
            if (cameraError?.name === "NotAllowedError") {
                setError("Camera access denied. Check browser permissions and try again.");
            } else if (cameraError?.name === "NotFoundError") {
                setError("No cameras found.");
            } else {
                console.error("Unable to load webcam devices", cameraError);
                setError(
                    "We could not access your camera. Check browser permissions and try again."
                );
            }
        } finally {
            setIsLoadingCameras(false);
            if (isOpenRef.current) {
                setHasLoadedCameras(true);
            }
        }
    }, [loadResolutionsForCamera]);

    const startCamera = useCallback(async () => {
        if (!isOpen || isRecording) return;

        const requestId = startCameraRequestRef.current + 1;
        startCameraRequestRef.current = requestId;
        setIsStartingCamera(true);
        setError(null);
        stopStream();

        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: false,
                video: getVideoConstraints(selectedDeviceId, resolution)
            });

            if (startCameraRequestRef.current !== requestId || !isOpenRef.current) {
                stream.getTracks().forEach((track) => track.stop());
                return;
            }

            streamRef.current = stream;

            if (videoRef.current) {
                videoRef.current.srcObject = stream;
                await videoRef.current.play();
            }
        } catch (cameraError: any) {
            if (startCameraRequestRef.current !== requestId || cameraError?.name === "AbortError") {
                return;
            }
            console.error("Unable to start webcam capture", cameraError);
            setError("We could not access your camera. Check browser permissions and try again.");
        } finally {
            if (startCameraRequestRef.current === requestId) {
                setIsStartingCamera(false);
            }
        }
    }, [getVideoConstraints, isOpen, isRecording, resolution, selectedDeviceId, stopStream]);

    useEffect(() => {
        if (!isOpen) return;

        loadCameras();

        const handleDeviceChange = () => {
            loadCameras();
        };

        navigator.mediaDevices.addEventListener("devicechange", handleDeviceChange);

        return () => {
            navigator.mediaDevices.removeEventListener("devicechange", handleDeviceChange);
            startCameraRequestRef.current += 1;
            setHasLoadedCameras(false);
            stopStream();
        };
    }, [isOpen, loadCameras, stopStream]);

    useEffect(() => {
        if (
            !isOpen ||
            !hasLoadedCameras ||
            isLoadingCameras ||
            isLoadingResolutions ||
            cameras.length === 0 ||
            error
        )
            return;
        startCamera();
    }, [
        cameras.length,
        error,
        hasLoadedCameras,
        isLoadingCameras,
        isLoadingResolutions,
        isOpen,
        startCamera
    ]);

    useEffect(() => {
        if (!isRecording) return;

        const intervalId = window.setInterval(() => {
            setRecordingSeconds((seconds) => Math.min(seconds + 1, MAX_VIDEO_DURATION));
        }, 1000);

        return () => window.clearInterval(intervalId);
    }, [isRecording]);

    async function handleDeviceChange(deviceId: string) {
        setError(null);
        setSelectedDeviceId(deviceId);
        await loadResolutionsForCamera(deviceId, resolution);
    }

    function handleResolutionChange(value: string) {
        setError(null);
        const option = resolutionOptions.find((item) => resolutionValue(item) === value);
        const nextResolution = option
            ? { width: option.width, height: option.height }
            : DEFAULT_RESOLUTION;
        setResolution(nextResolution);
    }

    const handleCapturePhoto = useCallback(async () => {
        const video = videoRef.current;
        if (!video || !video.videoWidth || !video.videoHeight) {
            setError("The camera is still warming up. Try again in a moment.");
            return;
        }

        const canvas = document.createElement("canvas");
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const context = canvas.getContext("2d");

        if (!context) {
            setError("We could not capture a photo in this browser.");
            return;
        }

        context.drawImage(video, 0, 0, canvas.width, canvas.height);

        const blob = await new Promise<Blob | null>((resolve) =>
            canvas.toBlob(resolve, "image/jpeg", 0.92)
        );

        if (!blob) {
            setError("We could not save that photo. Try again.");
            return;
        }

        onCapture([new File([blob], `webcam-${Date.now()}.jpg`, { type: "image/jpeg" })]);
        closeAndReset();
    }, [closeAndReset, onCapture]);

    const stopRecording = useCallback(() => {
        clearStopTimeout();

        if (recorderRef.current?.state === "recording") {
            recorderRef.current.stop();
        }
    }, [clearStopTimeout]);

    const startRecording = useCallback(() => {
        if (!streamRef.current || !canRecordVideo) {
            setError("Video recording is not supported in this browser.");
            return;
        }

        clearStopTimeout();
        chunksRef.current = [];
        setError(null);
        setRecordingSeconds(0);

        const recorder = new MediaRecorder(streamRef.current, {
            mimeType: supportedVideoMimeType || undefined
        });

        recorderRef.current = recorder;
        recorder.ondataavailable = (event) => {
            if (event.data.size > 0) {
                chunksRef.current.push(event.data);
            }
        };
        recorder.onstop = () => {
            clearStopTimeout();
            const uploadMimeType = getUploadVideoMimeType(supportedVideoMimeType);
            const blob = new Blob(chunksRef.current, { type: uploadMimeType });
            chunksRef.current = [];
            setIsRecording(false);
            setRecordingSeconds(0);

            if (blob.size === 0) {
                setError("We could not save that recording. Try again.");
                return;
            }

            onCapture([
                new File(
                    [blob],
                    `webcam-${Date.now()}.${uploadMimeType === "video/mp4" ? "mp4" : "webm"}`,
                    { type: uploadMimeType }
                )
            ]);
            closeAndReset();
        };

        recorder.start();
        setIsRecording(true);
        stopTimeoutRef.current = window.setTimeout(stopRecording, MAX_VIDEO_DURATION * 1000);
    }, [
        canRecordVideo,
        clearStopTimeout,
        closeAndReset,
        onCapture,
        stopRecording,
        supportedVideoMimeType
    ]);

    const remainingSeconds = Math.max(MAX_VIDEO_DURATION - recordingSeconds, 0);
    const controlsDisabled = isRecording || isLoadingCameras || isLoadingResolutions;
    const selectedCameraLabel =
        cameras
            .find((camera) => camera.deviceId === selectedDeviceId)
            ?.label.replace(/\(.+\)/, "")
            .trim() || "Select camera";
    const selectedResolutionLabel =
        resolutionOptions.find(
            (option) => resolution && resolutionValue(option) === resolutionValue(resolution)
        )?.label || resolutionLabel(resolution);
    const cameraItems: DropdownItem[] = cameras.map((camera, index) => ({
        text: getCameraLabel(camera, index),
        value: camera.deviceId
    }));
    const resolutionItems: DropdownItem[] = resolutionOptions.map((option) => ({
        text: option.label,
        value: resolutionValue(option)
    }));

    const photoDisabled = isStartingCamera || Boolean(error);
    const recordDisabled = isStartingCamera || !canRecordVideo || Boolean(error);
    const primaryActionDisabled = mode === "photo" ? photoDisabled : recordDisabled;
    const primaryActionLabel = mode === "photo" ? "Take photo" : isRecording ? "Stop" : "Record";
    const primaryActionIcon =
        mode === "photo" ? "fas fa-camera" : isRecording ? "fas fa-stop" : "fas fa-record-vinyl";
    const isStopAction = mode === "video" && isRecording;

    return (
        <RfModal isOpen={isOpen} onClose={closeAndReset} disableSubmitOnEnter>
            <RfModal.Overlay className="bg-black/50" />
            <RfModal.Panel
                className={twClassNames(
                    "max-w-3xl gap-5 !rounded-2xl border border-white/70 !bg-white/95 ring-1 ring-violet-950/5 backdrop-blur-md",
                    "shadow-[0_1px_0_rgba(255,255,255,0.9)_inset,0_12px_40px_-12px_rgba(91,33,182,0.18)]"
                )}
            >
                <RfModal.CloseButton />

                <div className="flex flex-col items-center text-center">
                    <Chip color="purple" className="mb-2">
                        Webcam
                    </Chip>
                    <h2 className="m-0 bg-gradient-to-b from-gray-900 to-gray-700 bg-clip-text text-2xl font-semibold tracking-tight text-transparent">
                        Capture training data
                    </h2>
                    <p className="mt-2 max-w-md text-pretty text-sm leading-relaxed text-gray-600">
                        Record a {MAX_VIDEO_DURATION}s clip or capture a still image from your
                        webcam.
                    </p>
                </div>

                <div className="mx-auto flex w-full max-w-xs items-center gap-1 rounded-lg bg-gray-100 p-1">
                    <button
                        type="button"
                        onClick={() => setMode("video")}
                        disabled={isRecording || !canRecordVideo}
                        className={twClassNames(
                            "flex flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-lg px-3 py-1.5 text-xs font-medium transition-all duration-150",
                            mode === "video"
                                ? "bg-white text-violet-900 shadow-sm ring-1 ring-violet-200/70"
                                : "text-gray-600 hover:bg-white/60 hover:text-gray-900",
                            !canRecordVideo && "cursor-not-allowed opacity-50"
                        )}
                    >
                        <i className="fas fa-video shrink-0 text-[10px]" />
                        Video
                    </button>
                    <button
                        type="button"
                        onClick={() => setMode("photo")}
                        disabled={isRecording}
                        className={twClassNames(
                            "flex flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-lg px-3 py-1.5 text-xs font-medium transition-all duration-150",
                            mode === "photo"
                                ? "bg-white text-violet-900 shadow-sm ring-1 ring-violet-200/70"
                                : "text-gray-600 hover:bg-white/60 hover:text-gray-900"
                        )}
                    >
                        <i className="fas fa-camera shrink-0 text-[10px]" />
                        Photo
                    </button>
                </div>

                <div className="aspect-video relative overflow-hidden rounded-xl border border-gray-100 bg-gray-950 shadow-sm ring-1 ring-violet-950/10">
                    <video
                        ref={videoRef}
                        autoPlay
                        playsInline
                        muted
                        className={twClassNames(
                            "h-full w-full object-cover",
                            isStartingCamera && "opacity-50"
                        )}
                    />
                    {(isStartingCamera || isLoadingCameras || isLoadingResolutions) && (
                        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-black/40 text-white">
                            <i className="fas fa-spinner-third fa-spin text-2xl text-violet-300" />
                            <span className="text-sm font-medium">Starting camera...</span>
                        </div>
                    )}
                    {isRecording && (
                        <div className="absolute left-3 top-3 rounded-full bg-destructive px-3 py-1 text-sm font-medium text-white shadow-lg ring-1 ring-red-400/40">
                            <span className="mr-2 inline-block h-2 w-2 animate-pulse rounded-full bg-white" />
                            {remainingSeconds}s left
                        </div>
                    )}
                    {!isRecording &&
                        !isStartingCamera &&
                        !isLoadingCameras &&
                        !isLoadingResolutions &&
                        !error && (
                            <div className="absolute bottom-3 left-3 flex items-center gap-2 rounded-full bg-black/60 px-3 py-1 text-xs font-medium text-white backdrop-blur">
                                <i className="fas fa-dot-circle animate-pulse text-green-400" />
                                {mode === "video" ? "Ready to record" : "Ready for photo"}
                            </div>
                        )}
                </div>

                {error && (
                    <InlineAlert level="danger" type="primary">
                        {error}
                    </InlineAlert>
                )}

                {resolutionWarning && !error && (
                    <InlineAlert level="info" type="primary">
                        {resolutionWarning}
                    </InlineAlert>
                )}

                <div className="flex flex-col gap-3 rounded-xl border border-gray-200/80 bg-white p-3.5 sm:p-4">
                    <div className="grid min-w-0 gap-2 sm:grid-cols-2">
                        <Dropdown
                            title={selectorTitle("far fa-video", selectedCameraLabel)}
                            items={cameraItems}
                            onItemSelected={(item) => handleDeviceChange(item.value)}
                            disabled={controlsDisabled || cameraItems.length === 0}
                            menuButtonSize="small"
                            fullWidth
                            usePortal
                            maxHeight="max-h-80"
                        />
                        <Dropdown
                            title={selectorTitle("far fa-expand", selectedResolutionLabel)}
                            items={resolutionItems}
                            onItemSelected={(item) => handleResolutionChange(item.value)}
                            disabled={controlsDisabled || resolutionItems.length === 0}
                            menuButtonSize="small"
                            fullWidth
                            usePortal
                            maxHeight="max-h-80"
                        />
                    </div>
                    <p className="m-0 text-xs leading-relaxed text-secondary">
                        {mode === "video"
                            ? `Videos stop automatically at ${MAX_VIDEO_DURATION}s.`
                            : "Photos are added immediately to your upload set."}
                    </p>
                </div>

                <div className="flex flex-col gap-2 sm:flex-row">
                    <Button
                        type="secondary"
                        size="medium"
                        onClick={closeAndReset}
                        className="flex-1"
                    >
                        Cancel
                    </Button>
                    <Button
                        type={isStopAction ? "danger" : "primary"}
                        size="medium"
                        onClick={
                            mode === "photo"
                                ? handleCapturePhoto
                                : isRecording
                                  ? stopRecording
                                  : startRecording
                        }
                        disabled={primaryActionDisabled}
                        className="flex-1"
                    >
                        <i className={twClassNames(primaryActionIcon)} />
                        {primaryActionLabel}
                    </Button>
                </div>
            </RfModal.Panel>
        </RfModal>
    );
};
